import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTheme } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'

import { getTypeformHiddenFields } from '@/lib/typeform'
import { paths } from '@/paths'
import { useHideHubspotWhileMounted } from '@/hooks/use-hubspot-chat-visibility'
import { useUser } from '@/hooks/use-user'
import { useWorkspaces } from '@/hooks/use-workspaces'
import { TypeformModal } from '@/components/dashboard/layout/nav/typeform-modal'

interface OpenTypeformParams {
	formId: string
	onNavClose?: () => void
}

interface TypeformNavContextValue {
	openTypeform: (params: OpenTypeformParams) => void
}

const TypeformNavContext = createContext<TypeformNavContextValue | undefined>(undefined)

export function TypeformNavProvider({ children }: { children: ReactNode }) {
	const navigate = useNavigate()
	const theme = useTheme()
	const isMobile = useMediaQuery(theme.breakpoints.down('lg'))
	const { user } = useUser()
	const { currentOperator } = useWorkspaces()
	const [open, setOpen] = useState(false)
	const [formId, setFormId] = useState<string | null>(null)

	const hidden = useMemo(
		() => getTypeformHiddenFields(user, currentOperator?.name ?? user?.portal_operator?.name),
		[user, currentOperator?.name, user?.portal_operator?.name]
	)

	const { hide: hideHubspot } = useHideHubspotWhileMounted(false)

	const openTypeform = useCallback(
		({ formId: id, onNavClose }: OpenTypeformParams) => {
			if (isMobile) {
				onNavClose?.()
				hideHubspot()
				navigate(paths.dashboard.help.typeform(id), { state: { hidden } })
				return
			}

			onNavClose?.()
			setFormId(id)
			setOpen(true)
		},
		[isMobile, hidden, navigate, hideHubspot]
	)

	const closeTypeform = useCallback(() => {
		setOpen(false)
		setFormId(null)
	}, [])

	const value = useMemo(() => ({ openTypeform }), [openTypeform])

	return (
		<TypeformNavContext.Provider value={value}>
			{children}
			{!isMobile ? (
				<TypeformModal open={open} formId={formId} hidden={hidden} onClose={closeTypeform} />
			) : null}
		</TypeformNavContext.Provider>
	)
}

export function useTypeformNav(): TypeformNavContextValue {
	const context = useContext(TypeformNavContext)

	if (!context) {
		throw new Error('useTypeformNav must be used within a TypeformNavProvider')
	}

	return context
}
